import _ from 'lodash'
import numeral from 'numeral'
import api, { APIResponse } from '../../../lib/commons/api'
import DateTimeVO from '../../../vo/DateTimeVO'
import { TreeSource } from '../../containers/BulkSheetView/lib/tree'
import { TreeRow } from '../../containers/BulkSheetView/model'
import { UserEntity } from '../../../domain/entity/UserEntity'

export enum WorkingHoursType {
  Attendance = 'ATTENDANCE',
  PaidLeave = 'PAID_LEAVE',
  PaidLeaveHalf = 'PAID_LEAVE_HALF',
  SpecialLeave = 'SPECIAL_LEAVE',
  Absence = 'ABSENCE',
  Holiday = 'HOLIDAY',
}

export enum RequestStatus {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  SENT_BACK = 'SENT_BACK',
}

export class ProxyRequestSkelton implements TreeSource<ProxyRequestSkelton> {
  uuid: string
  children: ProxyRequestSkelton[]
  workDate: string
  schedule?: ProxyRequestScheduleBody
  actual?: ProxyRequestActualBody
  requestCheck?: boolean
  requestStatus?: RequestStatusRowProjectBody
}

export interface ProxyRequestBody {
  workDate: string
  schedule?: ProxyRequestScheduleBody
  actual?: ProxyRequestActualBody
}

export interface ProxyRequestScheduleBody {
  workingHoursType: string
  workHour: number
  legal: boolean
  dateName: string
}

export interface ProxyRequestActualBody {
  uuid: string
  workingHoursType: string
  workStartAt: string
  workEndAt: string
  breakTime: number
  remarks: string
}

export interface TaskActualWorkRow extends TreeRow {
  type: 'WORK_HOUR'
  date?: string
  workingHoursType?: string
  remarks?: string
  schedule?: ProxyRequestRowScheduleBody
  actual?: ProxyRequestRowActualBody
  totalActualHour?: number
  projectHours?: ProxyRequestRowProjectBody[]
  projectRequestStatus?: RequestStatusRowProjectBody[]
  approveCheck?: boolean
  sentBackCheck?: boolean
}

export interface ProxyRequestRow extends TreeRow {
  type: 'WORK_HOUR'
  date?: string
  workingHoursType?: string
  remarks?: string
  schedule?: ProxyRequestRowScheduleBody
  actual?: ProxyRequestRowActualBody
  totalActualHour?: number
  projectHours?: ProxyRequestRowProjectBody[]
  requestCheck?: boolean
  projectRequestStatus?: RequestStatusRowProjectBody[]
  proxyUser?: UserEntity
}

export interface StandardWorkingInfo {
  standardWorkDays: number
  standardWorkHours: number
  totalWorkDays: number
  totalWorkHours: number
  diffDays: number
  diffHours: number
  diffHoursPercent: number
  diffDaysPercent: number
}

export class ProxyRequestRowScheduleBody {
  workingHoursType?: string
  workHour?: number
  dateName?: string
  legal?: boolean
  constructor(src: ProxyRequestScheduleBody) {
    this.workingHoursType = src.workingHoursType
    this.workHour = src.workHour ?? 0
    this.dateName = src.dateName
    this.legal = src.legal
  }
}

export class ProxyRequestRowActualBody {
  workingHoursType?: string
  actualUuid?: string
  startTime?: string
  endTime?: string
  breakTime?: string
  workedTime?: string
  remarks?: string

  constructor(src: ProxyRequestActualBody) {
    this.workingHoursType = src.workingHoursType
    this.actualUuid = src.uuid
    this.startTime = _.isEmpty(src.workStartAt)
      ? ''
      : formatTimeString(src.workStartAt)
    this.endTime = _.isEmpty(src.workEndAt)
      ? ''
      : formatTimeString(src.workEndAt)
    this.breakTime = restoreBreakTime(src.breakTime)
    this.workedTime = DefaultTime
    this.remarks = src.remarks
  }
}

export class ProxyRequestRowProjectBody {
  projectUuid: string
  actualHour: number = 0
}

export class RequestStatusRowProjectBody {
  projectUuid: string
  requestStatus: RequestStatus | undefined
  requestUuid?: string
  proxyUser?: UserEntity
}

export class ProxyRequestSummaryRowProjectBody {
  projectUuid: string
  actualHour: number = 0

  constructor(projectUuid: string) {
    this.projectUuid = projectUuid
  }
}

export class RequestRatioRowProjectBody {
  projectUuid: string
  requestStatusConunt: number
  approvedStatusConunt: number

  constructor(projectUuid: string) {
    this.projectUuid = projectUuid
    this.requestStatusConunt = 0
    this.approvedStatusConunt = 0
  }
}

export class ProxyRequestSummaryRow {
  type: 'SUM'
  breakTime: number
  workHour: number
  totalActualHour: number
  projectHours: ProxyRequestSummaryRowProjectBody[]
  constructor() {
    this.type = 'SUM'
    this.workHour = 0
    this.totalActualHour = 0
  }
}

export class ProxyRequestRatioRow {
  type: 'RATIO'
  totalActualHour: number
  totalActualHourRatio: number
  projectHours: ProxyRequestSummaryRowProjectBody[]
  projectRequestStatus: RequestRatioRowProjectBody[]
  constructor() {
    this.type = 'RATIO'
    this.totalActualHour = 0
    this.totalActualHourRatio = 0
  }
}

export type ProxyRequestRowIncludingCalculated =
  | ProxyRequestRow
  | ProxyRequestSummaryRow
  | ProxyRequestRatioRow

export const fetchProxyRequest = async (params: {
  projectUuid: string
  userUuid: string
  startDate: string
  endDate: string
}): Promise<APIResponse> => {
  return api.functional.request('GET', '/api/v1/actual_working_hours', params)
}

export interface ProxyRequestInput {
  uuid: string
  userUuid: string
  workDate: string
  workingHoursType: string
  workStartAt: string
  workEndAt: string
  breakTime: number
  remarks: string
}

export interface ProxyRequestBatchInput {
  added: ProxyRequestInput[]
  edited: ProxyRequestInput[]
  deleted: string[]
}

export interface ProxyRequestRequestInput {
  userUuid: string
  workDate: string
  projectRequests: RequestStatusRowProjectBody[]
}

export interface TaskProxyRequestApproveRequestInput {
  date: string
  userUuid: string
  projectUuid: string
  requestUuid: string
}

export interface ProxyRequestRequestBatchInput {
  application: ProxyRequestRequestInput[]
  reapplication: ProxyRequestRequestInput[]
  cancel: ProxyRequestRequestInput[]
  proxyUserUuid: string
}

export interface ProxyRequestApprovalBatchInput {
  approve: TaskProxyRequestApproveRequestInput[]
  sentBack: TaskProxyRequestApproveRequestInput[]
}

export interface FailedRequestStatusUuids {
  alreadySentBackUuid?: string
  alreadyApprovedUuid?: string
  alreadyCancelledUuid?: string
}

export interface ApproveActualWorkingResponse {
  uuid: string
  projectUuid: string
  failedRequestStatusUuids?: FailedRequestStatusUuids
}

export interface SentBackActualWorkResponse {
  uuid: string
  projectUuid: string
  failedRequestStatusUuids?: FailedRequestStatusUuids
}

export async function updateBatch(
  request: ProxyRequestBatchInput
): Promise<APIResponse> {
  return api.functional.request(
    'POST',
    '/api/v1/actual_working_hours/batch',
    request
  )
}

export async function updateRequestBatch(
  request: ProxyRequestRequestBatchInput | ProxyRequestApprovalBatchInput
): Promise<APIResponse> {
  return api.functional.request(
    'POST',
    '/api/v1/actual_working_hours/proxy/request',
    request
  )
}

export const fetchTargetUsers = async (params: {
  startDate: string
  endDate: string
}): Promise<APIResponse> => {
  return api.functional.request(
    'GET',
    '/api/v1/projects/resource_plans/users',
    params
  )
}

const DefaultTime = '0:00'

const restoreBreakTime = (time: number | undefined): string => {
  if (!time || time < 0) return ''
  return formatTime(time)
}

const formatHourMinute = (time: HourMinutes): string => {
  return `${numeral(time.hour).format('0')}:${numeral(time.minute).format(
    '00'
  )}`
}

export const formatTime = (time: number): string => {
  if (time <= 0) return DefaultTime
  const hour = Math.floor(time / 3600)
  const minute = (time % 3600) / 60
  return formatHourMinute({ hour, minute })
}

export const formatHour = (hour: number): string => {
  if (hour <= 0) return DefaultTime
  const minute = (hour * 60) % 60
  return formatHourMinute({ hour: Math.floor(hour), minute })
}

export const formatTimeString = (time: string): string => {
  const hourMinutes: HourMinutes | undefined = splitHourMinute(time)
  if (!hourMinutes) return DefaultTime
  return formatHourMinute(hourMinutes)
}

export const convertInput2Date = (
  date: string | undefined,
  time: string | undefined
): string => {
  if (!date || !time) return ''
  const baseTime = new DateTimeVO(date).toNumberValue()
  const timespan = splitHourMinute(time)
  if (!timespan) return ''
  const calcTime =
    baseTime + timespan.hour * 3600 * 1000 + timespan.minute * 60 * 1000
  return String(new DateTimeVO(calcTime).serialize())
}

export const convertBreakTime = (data: ProxyRequestRow): number => {
  const breakTime = calculateBreakTime(data)
  return breakTime
}

export const calculateBreakTime = (data: ProxyRequestRow): number => {
  if (!data.actual || !data.actual.breakTime) return 0
  const breakTimeArray = data.actual.breakTime.split(':')
  let breakTimeSpan = 0
  if (breakTimeArray.length > 0) {
    breakTimeSpan = Number(breakTimeArray[0]) * 60 * 60
  }
  if (breakTimeArray.length > 1) {
    breakTimeSpan += Number(breakTimeArray[1]) * 60
  }
  return breakTimeSpan
}

interface HourMinutes {
  hour: number
  minute: number
}

const splitHourMinute = (time: string): HourMinutes | undefined => {
  if (!time) return undefined
  const splits = time.split(':')
  return {
    hour: Number(splits[0]) ?? 0,
    minute: Number(splits[1]) ?? 0,
  }
}

export const formatWorkTime = (data: ProxyRequestRow): string => {
  const time = calculateWorkTime(data)
  if (time === 0) return ''
  return formatTime(time)
}

export const calculateWorkTime = (data: ProxyRequestRow): number => {
  if (!data.actual?.startTime || !data.actual?.endTime) {
    return 0
  }
  const totalWorks = calculateTotalWorkTime(data)
  const breakTime = calculateBreakTime(data)
  return Math.max(0, totalWorks - breakTime)
}

const calculateTotalWorkTime = (data: ProxyRequestRow): number => {
  const baseDate = new DateTimeVO(data.date!).toNumberValue() / 1000
  const startTerm = splitHourMinute(data.actual!.startTime!)
  const endTerm = splitHourMinute(data.actual!.endTime!)
  if (!startTerm || !endTerm) return 0
  const startTime = baseDate + startTerm.hour * 3600 + startTerm.minute * 60
  let endHour = endTerm.hour
  if (endHour < startTerm.hour) {
    endHour += 24
  }
  const endTime = baseDate + endHour * 3600 + endTerm.minute * 60
  return endTime - startTime
}

export const formatDiffTime = (diff: number) => {
  if (diff < 0) {
    const value = formatTime(diff * -1)
    return `-${value}`
  }
  return formatTime(diff)
}

export const isWorkingDay = (type: WorkingHoursType): boolean => {
  return [WorkingHoursType.Attendance, WorkingHoursType.PaidLeaveHalf].includes(
    type
  )
}

export const hasWorkingHours = (data: ProxyRequestRow): boolean => {
  if (data.totalActualHour && data.totalActualHour > 0) {
    return true
  }

  if (data.projectHours && data.projectHours.length > 0) {
    return data.projectHours.some(p => p.actualHour && p.actualHour > 0)
  }

  return false
}
